import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context';
import { useKeyboardShortcuts } from '@/hooks';
import {
  institutionDashboard,
  type DashboardStats,
  type RecentDocument,
  type PendingTask,
  type ActivityItem,
} from '@/services/institution';
import { archivalLegalHolds, archivalTransfers, archivalDispositions, type LegalHoldStats, type ArchiveTransferStats, type DispositionStats } from '@/services/archival';
import { retirementService, type RetirementStats, type ApproachingRetirement } from '@/services/retirement';
import { Button, KeyboardShortcutsModal, SkeletonStatCard, SkeletonListItem } from '@/components/common';
import styles from './DashboardPage.module.css';

// Time-based greeting
function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

// Format date relative to now
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return diffMins <= 1 ? 'Just now' : `${diffMins} minutes ago`;
    }
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

// Get due status for tasks
function getDueStatus(dueAt: string | null): { label: string; className: string } {
  if (!dueAt) return { label: 'No due date', className: styles.noDue };

  const due = new Date(dueAt);
  const now = new Date();
  const diffMs = due.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return { label: 'Overdue', className: styles.overdue };
  if (diffDays === 0) return { label: 'Due today', className: styles.dueToday };
  if (diffDays === 1) return { label: 'Due tomorrow', className: styles.dueSoon };
  if (diffDays <= 3) return { label: `Due in ${diffDays} days`, className: styles.dueSoon };
  return { label: `Due ${due.toLocaleDateString()}`, className: styles.noDue };
}

// Activity icons
function getActivityIcon(action: string): React.ReactNode {
  switch (action) {
    case 'CREATE':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case 'UPDATE':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      );
    case 'DELETE':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      );
    case 'APPROVE':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      );
    case 'REJECT':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    case 'UPLOAD':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      );
    default:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
  }
}

function getActivityColor(action: string): string {
  switch (action) {
    case 'CREATE': return 'var(--success)';
    case 'UPDATE': return 'var(--info)';
    case 'DELETE': return 'var(--error)';
    case 'APPROVE': return 'var(--success)';
    case 'REJECT': return 'var(--error)';
    case 'UPLOAD': return 'var(--primary)';
    default: return 'var(--text-muted)';
  }
}

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentDocs, setRecentDocs] = useState<RecentDocument[]>([]);
  const [pendingTasks, setPendingTasks] = useState<PendingTask[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  // New capability stats
  const [legalHoldStats, setLegalHoldStats] = useState<LegalHoldStats | null>(null);
  const [transferStats, setTransferStats] = useState<ArchiveTransferStats | null>(null);
  const [dispositionStats, setDispositionStats] = useState<DispositionStats | null>(null);
  const [retirementStats, setRetirementStats] = useState<RetirementStats | null>(null);
  const [approachingRetirements, setApproachingRetirements] = useState<ApproachingRetirement[]>([]);
  const [capabilityLoading, setCapabilityLoading] = useState(true);

  const greeting = useMemo(() => getGreeting(), []);

  const { shortcuts, showHelp, setShowHelp } = useKeyboardShortcuts({
    onOpenSearch: () => navigate('/search'),
  });

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [statsRes, docsRes, tasksRes, activityRes] = await Promise.all([
          institutionDashboard.getStats(),
          institutionDashboard.getRecentDocuments(),
          institutionDashboard.getPendingTasks(),
          institutionDashboard.getActivity(),
        ]);

        if (statsRes.success && statsRes.data) setStats(statsRes.data);
        if (docsRes.success && docsRes.data) setRecentDocs(docsRes.data);
        if (tasksRes.success && tasksRes.data) setPendingTasks(tasksRes.data);
        if (activityRes.success && activityRes.data) setActivity(activityRes.data);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      } finally {
        setLoading(false);
      }
    }

    async function fetchCapabilityData() {
      setCapabilityLoading(true);
      try {
        const [holdRes, transferRes, dispositionRes, retirementRes, approachingRes] = await Promise.all([
          archivalLegalHolds.getStats(),
          archivalTransfers.getStats(),
          archivalDispositions.getStats(),
          retirementService.getStats(),
          retirementService.getApproaching(3), // 90 days
        ]);

        if (holdRes.success && holdRes.data) setLegalHoldStats(holdRes.data);
        if (transferRes.success && transferRes.data) setTransferStats(transferRes.data);
        if (dispositionRes.success && dispositionRes.data) setDispositionStats(dispositionRes.data);
        if (retirementRes.success && retirementRes.data) setRetirementStats(retirementRes.data);
        if (approachingRes.success && approachingRes.data) setApproachingRetirements(approachingRes.data);
      } catch (err) {
        console.error('Failed to fetch capability data:', err);
      } finally {
        setCapabilityLoading(false);
      }
    }

    fetchData();
    fetchCapabilityData();
  }, []);

  // Calculate compliance score
  const complianceScore = useMemo(() => {
    if (!dispositionStats || !legalHoldStats) return null;
    const total = dispositionStats.total || 1;
    const onHold = dispositionStats.onHold || 0;
    const overdue = dispositionStats.overdueCount || 0;
    const penalty = ((overdue * 10) + (onHold * 2)) / total * 100;
    return Math.max(0, Math.min(100, 100 - penalty));
  }, [dispositionStats, legalHoldStats]);

  return (
    <div className={styles.page}>
      {/* Header Section */}
      <div className={styles.header} data-tour-target="dashboard-header">
        <div className={styles.headerContent}>
          <h1 className={styles.title}>{greeting}, {user?.firstName}!</h1>
          <p className={styles.subtitle}>Here's what's happening in your records management system today.</p>
        </div>
        <div className={styles.headerActions}>
          <Button variant="ghost" size="sm" onClick={() => setShowHelp(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="4" y="4" width="6" height="6" rx="1" />
              <rect x="14" y="4" width="6" height="6" rx="1" />
              <rect x="4" y="14" width="6" height="6" rx="1" />
              <rect x="14" y="14" width="6" height="6" rx="1" />
            </svg>
            Shortcuts
          </Button>
        </div>
      </div>

      {/* Quick Actions - 5 Cards */}
      <div className={styles.quickActions} data-tour-target="dashboard-quick-actions">
        <Link to="/records" className={styles.quickAction}>
          <div className={styles.quickActionIcon} style={{ backgroundColor: 'rgba(0, 107, 56, 0.1)', color: 'var(--primary)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </div>
          <div className={styles.quickActionContent}>
            <span className={styles.quickActionTitle}>Create Record</span>
            <span className={styles.quickActionDesc}>Add a new staff record</span>
          </div>
          <svg className={styles.quickActionArrow} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </Link>

        <Link to="/search" className={styles.quickAction}>
          <div className={styles.quickActionIcon} style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: 'var(--info)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </div>
          <div className={styles.quickActionContent}>
            <span className={styles.quickActionTitle}>Search</span>
            <span className={styles.quickActionDesc}>Find records quickly</span>
          </div>
          <span className={styles.shortcutHint}>Ctrl+K</span>
          <svg className={styles.quickActionArrow} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </Link>

        <Link to="/tasks" className={styles.quickAction}>
          <div className={styles.quickActionIcon} style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', color: 'var(--warning)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 5H20M9 12H20M9 19H20M4 5H4.01M4 12H4.01M4 19H4.01" />
            </svg>
          </div>
          <div className={styles.quickActionContent}>
            <span className={styles.quickActionTitle}>View Tasks</span>
            <span className={styles.quickActionDesc}>
              {stats?.pendingTasks ? `${stats.pendingTasks} pending` : 'Workflow tasks'}
            </span>
          </div>
          {stats?.pendingTasks ? (
            <span className={styles.taskBadge}>{stats.pendingTasks}</span>
          ) : null}
          <svg className={styles.quickActionArrow} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </Link>

        <Link to="/records" className={`${styles.quickAction} ${styles.quickActionAI}`}>
          <div className={styles.quickActionIcon} style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', color: '#8B5CF6' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2a4 4 0 0 1 4 4v2h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h2V6a4 4 0 0 1 4-4z" />
              <circle cx="9" cy="14" r="1.5" />
              <circle cx="15" cy="14" r="1.5" />
            </svg>
            <span className={styles.aiSparkle}>✦</span>
          </div>
          <div className={styles.quickActionContent}>
            <span className={styles.quickActionTitle}>AI Classify</span>
            <span className={styles.quickActionDesc}>Auto-categorize records</span>
          </div>
          <svg className={styles.quickActionArrow} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </Link>

        <Link to="/support" className={styles.quickAction}>
          <div className={styles.quickActionIcon} style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10B981' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              <path d="M8 10h.01M12 10h.01M16 10h.01" />
            </svg>
          </div>
          <div className={styles.quickActionContent}>
            <span className={styles.quickActionTitle}>Get Support</span>
            <span className={styles.quickActionDesc}>AI-powered help</span>
          </div>
          <svg className={styles.quickActionArrow} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </Link>
      </div>

      {/* Stats Row 1 - Original Stats */}
      <div className={styles.statsRow} data-tour-target="dashboard-stats">
        {loading ? (
          <>
            <SkeletonStatCard />
            <SkeletonStatCard />
            <SkeletonStatCard />
            <SkeletonStatCard />
          </>
        ) : (
          <>
            <div className={`${styles.statCard} ${styles.statCardAnimated}`} style={{ animationDelay: '0ms' }}>
              <div className={styles.statIcon} style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: 'var(--info)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M4 4C4 2.89543 4.89543 2 6 2H14L20 8V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{stats?.totalDocuments ?? 0}</span>
                <span className={styles.statLabel}>Total Records</span>
              </div>
            </div>

            <div className={`${styles.statCard} ${styles.statCardAnimated}`} style={{ animationDelay: '50ms' }}>
              <div className={styles.statIcon} style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', color: 'var(--warning)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 5H20M9 12H20M9 19H20M4 5H4.01M4 12H4.01M4 19H4.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{stats?.pendingTasks ?? 0}</span>
                <span className={styles.statLabel}>Pending Tasks</span>
              </div>
            </div>

            <div className={`${styles.statCard} ${styles.statCardAnimated}`} style={{ animationDelay: '100ms' }}>
              <div className={styles.statIcon} style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: 'var(--success)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12L11 14L15 10M12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{stats?.completedThisWeek ?? 0}</span>
                <span className={styles.statLabel}>Completed This Week</span>
              </div>
            </div>

            <div className={`${styles.statCard} ${styles.statCardAnimated}`} style={{ animationDelay: '150ms' }}>
              <div className={styles.statIcon} style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: 'var(--error)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{stats?.overdueTasks ?? 0}</span>
                <span className={styles.statLabel}>Overdue</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Stats Row 2 - New Capability Stats */}
      <div className={styles.statsRow}>
        {capabilityLoading ? (
          <>
            <SkeletonStatCard />
            <SkeletonStatCard />
            <SkeletonStatCard />
            <SkeletonStatCard />
          </>
        ) : (
          <>
            <div className={`${styles.statCard} ${styles.statCardAnimated}`} style={{ animationDelay: '200ms' }}>
              <div className={styles.statIcon} style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', color: '#DC2626' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{legalHoldStats?.active ?? 0}</span>
                <span className={styles.statLabel}>Active Legal Holds</span>
              </div>
              {(legalHoldStats?.active ?? 0) > 0 && <span className={styles.statBadgeWarning}>Active</span>}
            </div>

            <div className={`${styles.statCard} ${styles.statCardAnimated}`} style={{ animationDelay: '250ms' }}>
              <div className={styles.statIcon} style={{ backgroundColor: 'rgba(168, 85, 247, 0.1)', color: '#A855F7' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M21 8v13H3V8M1 3h22v5H1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 12h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{(transferStats?.submitted ?? 0) + (transferStats?.approved ?? 0)}</span>
                <span className={styles.statLabel}>Pending Archival</span>
              </div>
            </div>

            <div className={`${styles.statCard} ${styles.statCardAnimated}`} style={{ animationDelay: '300ms' }}>
              <div className={styles.statIcon} style={{ backgroundColor: 'rgba(236, 72, 153, 0.1)', color: '#EC4899' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                  <path d="M16 11l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{retirementStats?.pending ?? 0}</span>
                <span className={styles.statLabel}>Retirement Cases</span>
              </div>
            </div>

            <div className={`${styles.statCard} ${styles.statCardAnimated} ${styles.statCardAI}`} style={{ animationDelay: '350ms' }}>
              <div className={styles.statIcon} style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', color: '#8B5CF6' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2a4 4 0 0 1 4 4v2h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h2V6a4 4 0 0 1 4-4z" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="9" cy="14" r="1.5" fill="currentColor"/>
                  <circle cx="15" cy="14" r="1.5" fill="currentColor"/>
                </svg>
              </div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>AI</span>
                <span className={styles.statLabel}>Classification Ready</span>
              </div>
              <span className={styles.aiSparkleSmall}>✦</span>
            </div>
          </>
        )}
      </div>

      {/* Compliance Overview Section */}
      <div className={styles.complianceSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Compliance Overview
          </h2>
        </div>
        <div className={styles.complianceGrid}>
          {/* Legal Hold Status */}
          <div className={styles.complianceCard}>
            <div className={styles.complianceCardHeader}>
              <span className={styles.complianceCardTitle}>Legal Hold Status</span>
              <Link to="/legal-holds" className={styles.complianceCardLink}>View All</Link>
            </div>
            <div className={styles.complianceCardContent}>
              <div className={styles.holdStatusGrid}>
                <div className={styles.holdStatusItem}>
                  <span className={styles.holdStatusDot} style={{ backgroundColor: '#DC2626' }} />
                  <span className={styles.holdStatusLabel}>Active</span>
                  <span className={styles.holdStatusValue}>{legalHoldStats?.active ?? 0}</span>
                </div>
                <div className={styles.holdStatusItem}>
                  <span className={styles.holdStatusDot} style={{ backgroundColor: '#10B981' }} />
                  <span className={styles.holdStatusLabel}>Released</span>
                  <span className={styles.holdStatusValue}>{legalHoldStats?.released ?? 0}</span>
                </div>
                <div className={styles.holdStatusItem}>
                  <span className={styles.holdStatusDot} style={{ backgroundColor: '#6B7280' }} />
                  <span className={styles.holdStatusLabel}>Expired</span>
                  <span className={styles.holdStatusValue}>{legalHoldStats?.expired ?? 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Disposition Calendar */}
          <div className={styles.complianceCard}>
            <div className={styles.complianceCardHeader}>
              <span className={styles.complianceCardTitle}>Disposition Schedule</span>
              <Link to="/archival" className={styles.complianceCardLink}>View All</Link>
            </div>
            <div className={styles.complianceCardContent}>
              <div className={styles.dispositionStats}>
                <div className={styles.dispositionStat}>
                  <span className={styles.dispositionStatValue}>{dispositionStats?.upcomingThisMonth ?? 0}</span>
                  <span className={styles.dispositionStatLabel}>Due This Month</span>
                </div>
                <div className={styles.dispositionStat}>
                  <span className={`${styles.dispositionStatValue} ${(dispositionStats?.overdueCount ?? 0) > 0 ? styles.dispositionStatWarning : ''}`}>
                    {dispositionStats?.overdueCount ?? 0}
                  </span>
                  <span className={styles.dispositionStatLabel}>Overdue</span>
                </div>
                <div className={styles.dispositionStat}>
                  <span className={styles.dispositionStatValue}>{dispositionStats?.onHold ?? 0}</span>
                  <span className={styles.dispositionStatLabel}>On Hold</span>
                </div>
              </div>
            </div>
          </div>

          {/* Compliance Score */}
          <div className={styles.complianceCard}>
            <div className={styles.complianceCardHeader}>
              <span className={styles.complianceCardTitle}>Compliance Score</span>
            </div>
            <div className={styles.complianceCardContent}>
              <div className={styles.complianceScoreWrapper}>
                <div className={styles.complianceScoreCircle}>
                  <svg viewBox="0 0 36 36" className={styles.complianceScoreSvg}>
                    <path
                      className={styles.complianceScoreBg}
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path
                      className={styles.complianceScoreFg}
                      strokeDasharray={`${complianceScore ?? 0}, 100`}
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                  </svg>
                  <span className={styles.complianceScoreText}>{Math.round(complianceScore ?? 0)}%</span>
                </div>
                <span className={styles.complianceScoreLabel}>
                  {(complianceScore ?? 0) >= 90 ? 'Excellent' : (complianceScore ?? 0) >= 70 ? 'Good' : 'Needs Attention'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Archival Pipeline & Retirement Queue */}
      <div className={styles.pipelineSection}>
        {/* Archival Pipeline */}
        <div className={styles.pipelineCard}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 8v13H3V8M1 3h22v5H1z" />
                <path d="M10 12h4" />
              </svg>
              Archival Pipeline
            </h2>
            <Link to="/archival" className={styles.sectionLink}>View All</Link>
          </div>
          <div className={styles.pipelineContent}>
            <div className={styles.pipelineSteps}>
              <div className={styles.pipelineStep}>
                <div className={`${styles.pipelineStepCircle} ${(transferStats?.draft ?? 0) > 0 ? styles.pipelineStepActive : ''}`}>
                  {transferStats?.draft ?? 0}
                </div>
                <span className={styles.pipelineStepLabel}>Draft</span>
              </div>
              <div className={styles.pipelineConnector} />
              <div className={styles.pipelineStep}>
                <div className={`${styles.pipelineStepCircle} ${(transferStats?.submitted ?? 0) > 0 ? styles.pipelineStepActive : ''}`}>
                  {transferStats?.submitted ?? 0}
                </div>
                <span className={styles.pipelineStepLabel}>Submitted</span>
              </div>
              <div className={styles.pipelineConnector} />
              <div className={styles.pipelineStep}>
                <div className={`${styles.pipelineStepCircle} ${(transferStats?.approved ?? 0) > 0 ? styles.pipelineStepActive : ''}`}>
                  {transferStats?.approved ?? 0}
                </div>
                <span className={styles.pipelineStepLabel}>Approved</span>
              </div>
              <div className={styles.pipelineConnector} />
              <div className={styles.pipelineStep}>
                <div className={`${styles.pipelineStepCircle} ${styles.pipelineStepSuccess}`}>
                  {transferStats?.completed ?? 0}
                </div>
                <span className={styles.pipelineStepLabel}>Completed</span>
              </div>
            </div>
            <div className={styles.pipelineBreakdown}>
              <div className={styles.pipelineBreakdownItem}>
                <span className={styles.pipelineBreakdownDot} style={{ backgroundColor: '#A855F7' }} />
                <span>PRAAD Transfers: {transferStats?.byType?.find(t => t.transferType === 'praad')?.count ?? 0}</span>
              </div>
              <div className={styles.pipelineBreakdownItem}>
                <span className={styles.pipelineBreakdownDot} style={{ backgroundColor: '#3B82F6' }} />
                <span>MDA Archive: {transferStats?.byType?.find(t => t.transferType === 'mda_archive')?.count ?? 0}</span>
              </div>
              <div className={styles.pipelineBreakdownItem}>
                <span className={styles.pipelineBreakdownDot} style={{ backgroundColor: '#EF4444' }} />
                <span>Destruction: {transferStats?.byType?.find(t => t.transferType === 'destruction')?.count ?? 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Retirement Queue */}
        <div className={styles.pipelineCard}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              Retirement Queue
            </h2>
            <Link to="/retirement" className={styles.sectionLink}>View All</Link>
          </div>
          <div className={styles.pipelineContent}>
            {approachingRetirements.length === 0 ? (
              <div className={styles.emptyState}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <p>No approaching retirements in the next 90 days</p>
              </div>
            ) : (
              <>
                <div className={styles.retirementList}>
                  {approachingRetirements.slice(0, 4).map((ret) => (
                    <div key={ret.staffId} className={styles.retirementItem}>
                      <div className={styles.retirementItemAvatar}>
                        {ret.firstName[0]}{ret.lastName[0]}
                      </div>
                      <div className={styles.retirementItemInfo}>
                        <span className={styles.retirementItemName}>{ret.firstName} {ret.lastName}</span>
                        <span className={styles.retirementItemMeta}>{ret.jobTitle || 'Staff'} • {ret.departmentName || 'Department'}</span>
                      </div>
                      <div className={styles.retirementItemDays}>
                        <span className={`${styles.retirementDaysCount} ${ret.daysUntilRetirement <= 30 ? styles.retirementDaysUrgent : ''}`}>
                          {ret.daysUntilRetirement}
                        </span>
                        <span className={styles.retirementDaysLabel}>days</span>
                      </div>
                    </div>
                  ))}
                </div>
                {approachingRetirements.length > 4 && (
                  <div className={styles.retirementMore}>
                    +{approachingRetirements.length - 4} more approaching
                  </div>
                )}
              </>
            )}
            <div className={styles.retirementSummary}>
              <div className={styles.retirementSummaryItem}>
                <span className={styles.retirementSummaryValue}>{retirementStats?.pending ?? 0}</span>
                <span className={styles.retirementSummaryLabel}>Pending</span>
              </div>
              <div className={styles.retirementSummaryItem}>
                <span className={styles.retirementSummaryValue}>{retirementStats?.pensionProcessing ?? 0}</span>
                <span className={styles.retirementSummaryLabel}>Pension Processing</span>
              </div>
              <div className={styles.retirementSummaryItem}>
                <span className={styles.retirementSummaryValue}>{retirementStats?.completed ?? 0}</span>
                <span className={styles.retirementSummaryLabel}>Completed</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className={styles.mainGrid}>
        {/* Recent Documents & Pending Tasks */}
        <div className={styles.sections}>
          <section className={styles.section} data-tour-target="dashboard-recent-docs">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Recent Records</h2>
              <Link to="/records" className={styles.sectionLink}>View all</Link>
            </div>
            {loading ? (
              <div className={styles.loadingList}>
                <SkeletonListItem />
                <SkeletonListItem />
                <SkeletonListItem />
              </div>
            ) : recentDocs.length === 0 ? (
              <div className={styles.emptyState}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M4 4C4 2.89543 4.89543 2 6 2H14L20 8V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M14 2V8H20" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                <p>No recent records</p>
                <Button variant="secondary" size="sm" onClick={() => navigate('/records')}>
                  Create your first record
                </Button>
              </div>
            ) : (
              <div className={styles.documentList}>
                {recentDocs.map((doc) => (
                  <Link key={doc.id} to={`/records`} className={styles.documentItem}>
                    <div className={styles.documentIcon}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M4 4C4 2.89543 4.89543 2 6 2H14L20 8V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M14 2V8H20" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    </div>
                    <div className={styles.documentInfo}>
                      <span className={styles.documentTitle}>{doc.title}</span>
                      <span className={styles.documentMeta}>
                        {doc.documentType.name} - {formatDate(doc.updatedAt)}
                      </span>
                    </div>
                    <span
                      className={styles.documentStatus}
                      style={{ backgroundColor: `${doc.status.color}20`, color: doc.status.color }}
                    >
                      {doc.status.name}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </section>

          <section className={styles.section} data-tour-target="dashboard-pending-tasks">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Pending Tasks</h2>
              <Link to="/tasks" className={styles.sectionLink}>View all</Link>
            </div>
            {loading ? (
              <div className={styles.loadingList}>
                <SkeletonListItem />
                <SkeletonListItem />
                <SkeletonListItem />
              </div>
            ) : pendingTasks.length === 0 ? (
              <div className={styles.emptyState}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12L11 14L15 10M12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21Z" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                <p>No pending tasks</p>
                <span className={styles.emptyHint}>You're all caught up!</span>
              </div>
            ) : (
              <div className={styles.taskList}>
                {pendingTasks.map((task) => {
                  const dueStatus = getDueStatus(task.dueAt);
                  return (
                    <Link key={task.id} to="/tasks" className={styles.taskItem}>
                      <div className={styles.taskContent}>
                        <span className={styles.taskAction}>{task.step.actionRequired}</span>
                        <span className={styles.taskDocument}>{task.document.title}</span>
                      </div>
                      <span className={`${styles.taskDue} ${dueStatus.className}`}>
                        {dueStatus.label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {/* Activity Feed */}
        <aside className={styles.activityFeed} data-tour-target="dashboard-activity">
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Recent Activity</h2>
          </div>
          {loading ? (
            <div className={styles.loadingActivity}>
              <SkeletonListItem />
              <SkeletonListItem />
              <SkeletonListItem />
              <SkeletonListItem />
            </div>
          ) : activity.length === 0 ? (
            <div className={styles.emptyActivity}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              <p>No recent activity</p>
            </div>
          ) : (
            <div className={styles.activityList}>
              {activity.map((item) => (
                <div key={item.id} className={styles.activityItem}>
                  <div
                    className={styles.activityIcon}
                    style={{ backgroundColor: `${getActivityColor(item.action)}15`, color: getActivityColor(item.action) }}
                  >
                    {getActivityIcon(item.action)}
                  </div>
                  <div className={styles.activityContent}>
                    <span className={styles.activityText}>
                      {item.user ? `${item.user.firstName} ${item.user.lastName}` : 'System'}
                      <span className={styles.activityAction}> {item.action.toLowerCase()}d</span>
                      {' '}{item.entityType === 'document' ? 'a record' : item.entityType}
                    </span>
                    <span className={styles.activityTime}>{formatDate(item.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        shortcuts={shortcuts}
      />
    </div>
  );
}
